import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ShoppingCart, CheckCircle2, Shield, BarChart, Search } from 'lucide-react';
import ChatPopup from '../components/ChatPopup';

const AmazonBrandRegistry = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const benefits = [
    'Proactive brand protection',
    'Enhanced product listings',
    'Access to A+ Content',
    'Sponsored Brands advertising',
    'Brand Analytics',
    'Amazon Stores',
    'Report violations tool',
    'Increased search visibility'
  ];

  const features = [
    { icon: Shield, title: 'Brand Protection', desc: 'Proactive monitoring and removal of counterfeit listings' },
    { icon: BarChart, title: 'Brand Analytics', desc: 'Access to detailed insights about customer behavior and market trends' },
    { icon: Search, title: 'Enhanced Search', desc: 'Improved visibility in Amazon search results for your branded products' }
  ];

  const requirements = [
    {
      title: 'Active Trademark',
      description: 'A registered and active text or image trademark from an eligible trademark office, including the USPTO.'
    },
    {
      title: 'Brand Representation',
      description: 'Authority to represent the brand and access to the products you want to enroll.'
    },
    {
      title: 'Product Verification',
      description: 'Ability to provide product images and demonstrate ownership of the brand.'
    }
  ];

  const faqs = [
    {
      question: 'What is Amazon Brand Registry?',
      answer: 'Amazon Brand Registry is a free program that helps brand owners protect their intellectual property and create better shopping experiences on Amazon. It provides access to powerful tools for brand protection and enhanced selling features.'
    },
    {
      question: 'Do I need a trademark to enroll?',
      answer: 'Yes, you need an active registered trademark from a participating trademark office, including the USPTO. We can help you register your trademark if you don\'t have one yet.'
    },
    {
      question: 'How long does enrollment take?',
      answer: 'If you already have a registered trademark, Brand Registry enrollment typically takes 1-2 weeks. If you need to register your trademark first, the complete process takes 8-12 months.'
    },
    {
      question: 'What benefits do I get?',
      answer: 'Brand Registry provides proactive brand protection, enhanced product listings, access to advertising tools, brand analytics, and the ability to create Amazon Stores.'
    }
  ];

  return (
    <div className="min-h-screen">
      <ChatPopup isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-amber-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto">
                <ShoppingCart className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Amazon Brand Registry Services
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Protect your brand on Amazon's marketplace and unlock powerful selling tools with Amazon Brand Registry enrollment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
                Get Started
              </Button>
              <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is Amazon Brand Registry?</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base">
                <p>
                  Amazon Brand Registry is a program designed to help brand owners protect their intellectual property rights on Amazon and provide a better customer experience. It gives you enhanced control over your product listings and access to powerful brand-building tools.
                </p>
                <p>
                  With Brand Registry, you can proactively search for and report violations of your intellectual property, create enhanced brand content, and gain access to powerful advertising and analytics tools that help grow your business on Amazon.
                </p>
              </div>
            </div>
            <div className="relative h-[300px] lg:h-auto">
              <img 
                src="https://images.unsplash.com/photo-1717343656288-a1c0c23ba2df" 
                alt="Professional e-commerce business environment for brand registry"
                className="rounded-2xl shadow-2xl w-full h-full max-h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Key Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful tools to protect and grow your brand on Amazon
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-orange-600 transition-colors">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Benefits of Enrollment</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
                <CheckCircle2 className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enrollment Requirements</h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-8">
            {requirements.map((req, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{req.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{req.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-gray-50 border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingCart className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Enroll in Brand Registry?
          </h2>
          <p className="text-xl text-orange-100 mb-8">
            Protect your brand and unlock powerful Amazon selling tools today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => setIsChatOpen(true)} className="bg-white text-orange-600 hover:bg-gray-100">
              Get Started
            </Button>
            <Button size="lg" onClick={() => setIsChatOpen(true)} variant="outline" className="border-white text-white hover:bg-white/10">
              Talk to an Expert
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandRegistry;
